import { useState } from "react";
import { X } from "@/components/logos/X";
import { TwitterAvatar } from "@/components/twitter/TwitterAvatar";
import { Persona } from "@/api/personas";
import { useApproveReplyMutation, useRejectReplyMutation } from "@/api/twitter";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { LoadingButton } from "@/components/LoadingButton";
import { Badge } from "@/components/ui/badge";
import TwitterMediaGrid from "@/components/twitter/TwitterMedia";
import {
  formatDate,
  formatNumber,
  processReplyData,
} from "@/components/twitter/twitterUtils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Check,
  Pencil,
  X as XIcon,
  LoaderCircle,
  UserCircle,
  MapPin,
  Calendar,
  Users,
} from "lucide-react";

interface PendingReplyCardProps {
  reply: any;
  personaData: Persona;
  onApprove?: () => void;
  onReject?: () => void;
  automatedReplies?: boolean;
}

const PendingReplyCard = ({
  reply,
  personaData,
  onApprove,
  onReject,
  automatedReplies = false,
}: PendingReplyCardProps) => {
  const { toast } = useToast();
  const [editedResponse, setEditedResponse] = useState(
    reply?.response_content || "",
  );
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  // Use React Query mutations
  const approveMutation = useApproveReplyMutation();
  const rejectMutation = useRejectReplyMutation();

  // Process data using our shared utility
  const processedData = processReplyData(reply);

  // Handle approve reply
  const handleApprove = async (customResponse = null) => {
    try {
      setIsApproving(true);

      await approveMutation.mutateAsync({
        docId: reply.doc_id,
        personaId: personaData.persona_id,
        editedResponse: customResponse ?? undefined,
      });

      toast({ description: "Reply approved and posted successfully" });

      onApprove?.();
      setShowEditDialog(false);
    } catch (error: any) {
      console.error("Error approving reply:", error);
      toast({
        description:
          "Failed to approve reply: " + (error.message || "Unknown error"),
        variant: "destructive",
      });
    } finally {
      setIsApproving(false);
    }
  };

  // Handle reject reply
  const handleReject = async () => {
    try {
      setIsRejecting(true);

      await rejectMutation.mutateAsync({
        docId: reply.doc_id,
        personaId: personaData.persona_id,
        reason: rejectionReason,
      });

      toast({ description: "Reply rejected successfully" });

      onReject?.();
      setShowRejectDialog(false);
    } catch (error: any) {
      console.error("Error rejecting reply:", error);
      toast({
        description:
          "Failed to reject reply: " + (error.message || "Unknown error"),
        variant: "destructive",
      });
    } finally {
      setIsRejecting(false);
    }
  };

  // Open tweet in new tab
  const openTweetInNewTab = () => {
    if (processedData?.tweetId && processedData.username) {
      window.open(
        `https://twitter.com/${processedData.username}/status/${processedData.tweetId}`,
        "_blank",
      );
    }
  };

  if (!processedData) {
    return null;
  }

  return (
    <div className="bg-background border rounded-2xl">
      {/* Original Tweet Section */}
      <div className="px-6 pt-4 pb-2 border-b border-border">
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className={
                automatedReplies
                  ? "bg-green-500/10 text-green-500"
                  : "bg-amber-500/10 text-amber-500"
              }
            >
              {automatedReplies ? "Auto-Reply" : "Pending Approval"}
            </Badge>
          </div>
          {/* X icon with no click handler */}
          <X width={16} height={16} fill="white" />
        </div>

        {/* Original message */}
        <div className="border rounded-xl p-3 bg-gray-100 text-black">
          <div className="flex items-start gap-2">
            {/* User Avatar with hyperlink */}
            <div className="flex-shrink-0">
              <a
                href={`https://twitter.com/${processedData.username}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block hover:opacity-90 transition-opacity"
              >
                {processedData.profileImage ? (
                  <img
                    src={processedData.profileImage}
                    alt={`${processedData.displayName}'s avatar`}
                    className="w-10 h-10 rounded-full"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                    <UserCircle className="w-5 h-5 text-gray-500" />
                  </div>
                )}
              </a>
            </div>

            <div className="flex-1 min-w-0">
              {/* User Info Header */}
              <div className="flex flex-col">
                <div className="flex items-start justify-between">
                  <div className="flex flex-col">
                    <div className="font-bold text-black line-clamp-1">
                      {processedData.displayName}
                      {processedData.verified && (
                        <span className="ml-1 inline-flex items-center justify-center bg-blue-500 text-white rounded-full w-4 h-4 text-sm">
                          ✓
                        </span>
                      )}
                    </div>
                    <div className="text-gray-500 text-sm">
                      @{processedData.username}
                    </div>
                  </div>
                  <span className="text-sm text-gray-500 flex-shrink-0">
                    {formatDate(processedData.timestamp)}
                  </span>
                </div>

                {/* User meta information (compact display) */}
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-gray-500 mt-1 mb-2">
                  {processedData.location && (
                    <div className="flex items-center">
                      <MapPin className="w-3 h-3 mr-1" />
                      <span>{processedData.location}</span>
                    </div>
                  )}
                  {processedData.joinedDate && (
                    <div className="flex items-center">
                      <Calendar className="w-3 h-3 mr-1" />
                      <span>
                        Joined{" "}
                        {formatDate(processedData.joinedDate, {
                          month: "long",
                          year: "numeric",
                          day: undefined,
                          hour: undefined,
                          minute: undefined,
                        })}
                      </span>
                    </div>
                  )}
                  {processedData.followersCount > 0 && (
                    <div className="flex items-center">
                      <Users className="w-3 h-3 mr-1" />
                      <span>
                        {formatNumber(processedData.followersCount)} followers
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Tweet content */}
              <p className="mt-1 text-black text-sm">
                {processedData.tweetText}
              </p>
            </div>
          </div>

          {/* Using our shared TwitterMediaGrid component */}
          <TwitterMediaGrid urls={processedData.mediaUrls} />

          {/* Thread context */}
          {processedData.threadContext?.length > 1 && (
            <div className="mt-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={openTweetInNewTab}
                className="text-gray-600 hover:text-white hover:bg-gray-800"
              >
                Show full conversation in new tab
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Response Content Section */}
      <div className="px-6 pt-4 pb-2">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <TwitterAvatar persona={personaData} />
          <div className="text-right">
            Replying to @{processedData.username}
          </div>
        </div>

        <p className="mt-2 whitespace-pre-line text-sm">
          {processedData.responseText}
        </p>
      </div>

      {/* Action Buttons - Only show if not in automated replies mode */}
      {!automatedReplies && (
        <div className="px-6 py-3 flex justify-end gap-2">
          <AlertDialog
            open={showRejectDialog}
            onOpenChange={setShowRejectDialog}
          >
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                className="text-destructive border-destructive hover:bg-destructive/10"
                disabled={isRejecting || isApproving}
              >
                {isRejecting ? (
                  <LoaderCircle className="w-4 h-4 animate-spin mr-1" />
                ) : (
                  <XIcon className="w-4 h-4 mr-1" />
                )}
                Reject
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Reject this reply?</AlertDialogTitle>
              </AlertDialogHeader>
              <div className="space-y-3">
                <p>This reply will be discarded and not posted to Twitter.</p>
                <Textarea
                  placeholder="Reason for rejection (optional)"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={3}
                  className="resize-none"
                />
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isRejecting}>
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction asChild>
                  <LoadingButton
                    variant="destructive"
                    loading={isRejecting}
                    onClick={(e) => {
                      e.preventDefault();
                      handleReject();
                    }}
                  >
                    Reject
                  </LoadingButton>
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog open={showEditDialog} onOpenChange={setShowEditDialog}>
            <AlertDialogTrigger asChild>
              <Button variant="outline" disabled={isRejecting || isApproving}>
                {isApproving && showEditDialog ? (
                  <LoaderCircle className="w-4 h-4 animate-spin mr-1" />
                ) : (
                  <Pencil className="w-4 h-4 mr-1" />
                )}
                Edit &amp; Approve
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="max-w-3xl">
              <AlertDialogHeader>
                <AlertDialogTitle>Edit Reply</AlertDialogTitle>
              </AlertDialogHeader>
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <TwitterAvatar persona={personaData} />
                  <div className="text-sm text-muted-foreground">
                    Replying to @{processedData.username}
                  </div>
                </div>
                <Textarea
                  value={editedResponse}
                  onChange={(e) => setEditedResponse(e.target.value)}
                  rows={6}
                  className="resize-none text-xs"
                />
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isApproving}>
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction asChild>
                  <LoadingButton
                    loading={isApproving}
                    onClick={(e) => {
                      e.preventDefault();
                      handleApprove(editedResponse);
                    }}
                  >
                    Approve &amp; Post
                  </LoadingButton>
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <LoadingButton
            loading={isApproving && !showEditDialog}
            onClick={() => handleApprove()}
            variant="default"
            disabled={isRejecting || (isApproving && showEditDialog)}
          >
            <Check className="w-4 h-4 mr-1" />
            Approve
          </LoadingButton>
        </div>
      )}
    </div>
  );
};

export default PendingReplyCard;
